/**
 * Task Redux Slice
 * Manages task state from WebSocket updates
 */

import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  items: [],
  pendingCount: 0,
  connected: false,
  lastUpdated: null,
};

const taskSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setTasks: (state, action) => {
      state.items = action.payload;
      state.pendingCount = action.payload.filter((t) => t.status === 'pending_acceptance').length;
      state.lastUpdated = new Date().toISOString();
    },

    addTask: (state, action) => {
      state.items.unshift(action.payload);

      if (action.payload.status === 'pending_acceptance') {
        state.pendingCount += 1;
      }

      state.lastUpdated = new Date().toISOString();
    },

    updateTask: (state, action) => {
      const index = state.items.findIndex((t) => t.id === action.payload.id);

      if (index !== -1) {
        const wasPending = state.items[index].status === 'pending_acceptance';
        const isPending = action.payload.status === 'pending_acceptance';

        state.items[index] = action.payload;

        // Adjust pending count
        if (wasPending && !isPending) {
          state.pendingCount = Math.max(0, state.pendingCount - 1);
        } else if (!wasPending && isPending) {
          state.pendingCount += 1;
        }

        state.lastUpdated = new Date().toISOString();
      }
    },

    removeTask: (state, action) => {
      const task = state.items.find((t) => t.id === action.payload.id);

      if (task && task.status === 'pending_acceptance') {
        state.pendingCount = Math.max(0, state.pendingCount - 1);
      }

      state.items = state.items.filter((t) => t.id !== action.payload.id);
      state.lastUpdated = new Date().toISOString();
    },

    setConnected: (state, action) => {
      state.connected = action.payload;
    },

    clearTasks: (state) => {
      state.items = [];
      state.pendingCount = 0;
      state.connected = false;
      state.lastUpdated = null;
    },
  },
});

export const { setTasks, addTask, updateTask, removeTask, setConnected, clearTasks } =
  taskSlice.actions;

// Selectors
export const selectTasks = (state) => state.tasks.items;
export const selectPendingCount = (state) => state.tasks.pendingCount;
export const selectTaskConnected = (state) => state.tasks.connected;

// Filtered selectors
export const selectMyTasks = (userId) => (state) =>
  state.tasks.items.filter((t) => t.assigned_to === userId);

export const selectCreatedTasks = (userId) => (state) =>
  state.tasks.items.filter((t) => t.created_by === userId);

export const selectTasksByStatus = (status) => (state) =>
  state.tasks.items.filter((t) => t.status === status);

export default taskSlice.reducer;
